import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddKitOutRequestSchema,
  CreateKitOutProtocolRequestSchema,
  type LabKitDto,
} from '@ctms/contracts';
import {
  AlertTriangle,
  ArchiveRestore,
  CheckCircle2,
  Plus,
  ScrollText,
  Trash2,
  X,
} from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface KitBasketDialogProps {
  trialId: number;
  /** kits:write — hides add/restore/protocol actions when false. */
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type BasketView = 'list' | 'add' | 'protocol';

/**
 * Virtual basket of kits awaiting destruction (REQ-KIT-030..032, Q-KIT-011).
 * The basket lists only entries without a protocol — binding an entry to a
 * destruction protocol permanently removes it from the basket and from stock;
 * a late restore attempt answers 409 with a Polish domain message.
 */
export function KitBasketDialog({
  trialId,
  canWrite,
  open,
  onOpenChange,
}: KitBasketDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const [view, setView] = useState<BasketView>('list');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add-to-basket form state
  const [labKitId, setLabKitId] = useState<number | ''>('');
  const [labKitExpirationId, setLabKitExpirationId] = useState<number | ''>('');
  const [numberOfKits, setNumberOfKits] = useState<number | ''>(1);
  const [outDate, setOutDate] = useState(new Date().toISOString().slice(0, 10));
  const [comments, setComments] = useState('');

  // Destruction-protocol form state
  const [protocolDate, setProtocolDate] = useState(new Date().toISOString().slice(0, 10));
  const [protocolComments, setProtocolComments] = useState('');

  const basketQuery = useQuery({
    queryKey: kitQueryKeys.basket.list({ trialId }),
    queryFn: () => kitsApi.listBasket({ trialId }),
    enabled: open && trialId > 0,
    staleTime: 0,
  });
  const basket = basketQuery.data ?? [];

  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: open && canWrite && trialId > 0,
    staleTime: 5 * 60 * 1000,
  });
  const laboratories = laboratoriesQuery.data?.items ?? [];
  const labIds = laboratories.map((l) => l.id);

  const kitsQuery = useQuery({
    queryKey: ['kits', 'by-trial-all-labs', trialId, labIds],
    queryFn: async (): Promise<LabKitDto[]> => {
      const allKits: LabKitDto[] = [];
      for (const lab of laboratories) {
        const page = await kitsApi.listKits({ trialLaboratoryId: lab.id, page: 1, limit: 100 });
        allKits.push(...page.items);
      }
      return allKits;
    },
    enabled: open && canWrite && laboratories.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const availableKits = kitsQuery.data ?? [];

  const selectedKit = availableKits.find((k) => k.id === labKitId);

  const expirationsQuery = useQuery({
    queryKey: kitQueryKeys.kits.expirations(labKitId === '' ? 0 : labKitId),
    queryFn: () => kitsApi.listExpirations(labKitId as number),
    enabled: labKitId !== '' && selectedKit?.expiration === true,
    staleTime: 5 * 60 * 1000,
  });
  const expirations = expirationsQuery.data ?? [];

  const addMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof kitsApi.addToBasket>[0]) =>
      kitsApi.addToBasket(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.basket.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      resetAddForm();
      setNotice('Pozycja odłożona do kosza wirtualnego.');
      setView('list');
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się odłożyć pozycji do kosza').then(setFormError);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: number) => kitsApi.restoreFromBasket(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.basket.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      setActionError(null);
      setNotice('Pozycja przywrócona na stan magazynowy.');
    },
    onError: (err: unknown) => {
      // 404 (unknown item) and 409 (already in a protocol) carry a Polish
      // domain message from the server — show it verbatim.
      void getApiErrorMessage(err, 'Nie udało się przywrócić pozycji').then(setActionError);
    },
  });

  const protocolMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof kitsApi.createProtocol>[0]) =>
      kitsApi.createProtocol(payload),
    onSuccess: (protocol) => {
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.protocols.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.basket.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      setSelectedIds([]);
      setFormError(null);
      setNotice(`Utworzono protokół zniszczenia #${protocol.id}.`);
      setView('list');
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się utworzyć protokołu zniszczenia').then(
        setFormError,
      );
    },
  });

  function resetAddForm(): void {
    setLabKitId('');
    setLabKitExpirationId('');
    setNumberOfKits(1);
    setOutDate(new Date().toISOString().slice(0, 10));
    setComments('');
    setFormError(null);
  }

  function toggleSelect(id: number): void {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function handleAddSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFormError(null);
    const parsed = AddKitOutRequestSchema.safeParse({
      labKitId,
      labKitExpirationId: labKitExpirationId === '' ? null : labKitExpirationId,
      numberOfKits,
      outDate,
      comments: comments.trim() || undefined,
    });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      setFormError(firstIssue ? firstIssue.message : 'Nieprawidłowe dane formularza');
      return;
    }
    addMutation.mutate(parsed.data);
  }

  function handleProtocolSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFormError(null);
    const labIdsOfSelected = new Set(
      basket
        .filter((entry) => selectedIds.includes(entry.id))
        .map((entry) => availableKits.find((k) => k.id === entry.labKitId)?.trialLaboratoryId)
        .filter((id): id is number => id !== undefined),
    );
    if (labIdsOfSelected.size !== 1) {
      setFormError(
        'Wybierz pozycje jednego laboratorium — protokół zniszczenia wystawia się dla laboratorium.',
      );
      return;
    }
    const parsed = CreateKitOutProtocolRequestSchema.safeParse({
      trialLaboratoryId: [...labIdsOfSelected][0],
      protocolDate,
      comments: protocolComments.trim() || undefined,
      basketItemIds: selectedIds,
    });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      setFormError(firstIssue ? firstIssue.message : 'Nieprawidłowe dane formularza');
      return;
    }
    protocolMutation.mutate(parsed.data);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="kit-basket-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-950 text-rose-400 border border-rose-800/60">
              <Trash2 className="h-5 w-5" />
            </div>
            <div>
              <h2 id="kit-basket-title" className="text-lg font-semibold text-slate-100">
                Kosz wirtualny — zestawy do zniszczenia
              </h2>
              <p className="text-xs text-slate-400">
                Pozycje odłożone z magazynu oczekujące na protokół zniszczenia (REQ-KIT-030..032)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {notice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-lg border border-emerald-900/60 bg-emerald-950/40 p-3 text-sm text-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>{notice}</span>
            </div>
          )}
          {actionError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
              <span>{actionError}</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              {basket.length === 0
                ? 'Kosz jest pusty'
                : `${basket.length} pozycji w koszu`}
              {selectedIds.length > 0 && ` · zaznaczono ${selectedIds.length}`}
            </span>
            <div className="flex items-center gap-2">
              {view === 'list' && canWrite && (
                <>
                  <button
                    type="button"
                    id="basket-add-button"
                    onClick={() => {
                      resetAddForm();
                      setNotice(null);
                      setView('add');
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Dodaj do kosza</span>
                  </button>
                  <button
                    type="button"
                    id="basket-create-protocol-button"
                    disabled={selectedIds.length === 0}
                    onClick={() => {
                      setFormError(null);
                      setView('protocol');
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-rose-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-40"
                  >
                    <ScrollText className="h-4 w-4" />
                    <span>Zniszcz zaznaczone ({selectedIds.length})</span>
                  </button>
                </>
              )}
              {view !== 'list' && (
                <button
                  type="button"
                  onClick={() => {
                    setFormError(null);
                    setView('list');
                  }}
                  className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Wróć do listy
                </button>
              )}
            </div>
          </div>

          {view === 'add' && (
            <form
              id="basket-add-form"
              noValidate
              onSubmit={handleAddSubmit}
              className="space-y-4 rounded-lg border border-slate-800 bg-slate-950/40 p-4"
            >
              <h3 className="text-sm font-semibold text-slate-200">
                Odłóż zestawy do kosza wirtualnego
              </h3>
              {formError && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-sm text-rose-300"
                >
                  <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
                  <span>{formError}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-xs">
                  <span className="text-slate-400">Typ zestawu *</span>
                  <select
                    aria-label="Typ zestawu laboratoryjnego"
                    value={labKitId}
                    onChange={(e) => {
                      setLabKitId(e.target.value === '' ? '' : Number(e.target.value));
                      setLabKitExpirationId('');
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">— wybierz —</option>
                    {availableKits.map((kit) => (
                      <option key={kit.id} value={kit.id}>
                        {kit.name}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedKit?.expiration === true && (
                  <label className="block text-xs">
                    <span className="text-slate-400">Partia (data ważności)</span>
                    <select
                      aria-label="Partia zestawu"
                      value={labKitExpirationId}
                      onChange={(e) =>
                        setLabKitExpirationId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    >
                      <option value="">— bez partii —</option>
                      {expirations.map((exp) => (
                        <option key={exp.id} value={exp.id}>
                          {exp.expirationDate}
                          {exp.batchComment ? ` — ${exp.batchComment}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="block text-xs">
                  <span className="text-slate-400">Liczba sztuk *</span>
                  <input
                    type="number"
                    min={1}
                    aria-label="Liczba odkładanych sztuk"
                    value={numberOfKits}
                    onChange={(e) =>
                      setNumberOfKits(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
                <label className="block text-xs">
                  <span className="text-slate-400">Data usunięcia ze stanu *</span>
                  <input
                    type="date"
                    aria-label="Data usunięcia ze stanu"
                    value={outDate}
                    onChange={(e) => setOutDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
              </div>
              <label className="block text-xs">
                <span className="text-slate-400">Uwagi</span>
                <textarea
                  aria-label="Uwagi do odłożenia"
                  rows={2}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                />
              </label>
              <button
                type="submit"
                disabled={addMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-xs font-semibold text-white hover:bg-cyan-500 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                <span>{addMutation.isPending ? 'Odkładanie…' : 'Odłóż do kosza'}</span>
              </button>
            </form>
          )}

          {view === 'protocol' && (
            <form
              id="basket-protocol-form"
              noValidate
              onSubmit={handleProtocolSubmit}
              className="space-y-4 rounded-lg border border-slate-800 bg-slate-950/40 p-4"
            >
              <h3 className="text-sm font-semibold text-slate-200">
                Protokół zniszczenia zaznaczonych pozycji
              </h3>
              {formError && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-sm text-rose-300"
                >
                  <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
                  <span>{formError}</span>
                </div>
              )}
              <ul className="space-y-1 text-xs text-slate-300">
                {basket
                  .filter((entry) => selectedIds.includes(entry.id))
                  .map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-center justify-between rounded border border-slate-800 bg-slate-900 px-3 py-1.5"
                    >
                      <span>{entry.kitName ?? `Zestaw #${entry.labKitId}`}</span>
                      <span className="font-mono">{entry.numberOfKits} szt.</span>
                    </li>
                  ))}
              </ul>
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-xs">
                  <span className="text-slate-400">Data zniszczenia *</span>
                  <input
                    type="date"
                    aria-label="Data zniszczenia"
                    value={protocolDate}
                    onChange={(e) => setProtocolDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
                <label className="block text-xs">
                  <span className="text-slate-400">Uwagi do protokołu</span>
                  <input
                    aria-label="Uwagi do protokołu"
                    value={protocolComments}
                    onChange={(e) => setProtocolComments(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={protocolMutation.isPending || selectedIds.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
              >
                <ScrollText className="h-4 w-4" />
                <span>
                  {protocolMutation.isPending
                    ? 'Tworzenie…'
                    : `Utwórz protokół zniszczenia (${selectedIds.length})`}
                </span>
              </button>
            </form>
          )}

          {view === 'list' &&
            (basketQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">Ładowanie kosza…</div>
            ) : basket.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                Kosz wirtualny jest pusty — brak pozycji oczekujących na zniszczenie.
              </div>
            ) : (
              <table className="w-full text-left text-xs text-slate-200" id="kit-basket-table">
                <thead className="border-b border-slate-800 bg-slate-950/60 text-[11px] uppercase text-slate-400">
                  <tr>
                    {canWrite && <th className="w-8 px-3 py-2"></th>}
                    <th className="px-3 py-2">Zestaw</th>
                    <th className="px-3 py-2">Laboratorium</th>
                    <th className="px-3 py-2">Partia</th>
                    <th className="px-3 py-2 text-right">Sztuk</th>
                    <th className="px-3 py-2">Data zdjęcia</th>
                    <th className="px-3 py-2">Uwagi</th>
                    {canWrite && <th className="px-3 py-2 text-right">Akcje</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {basket.map((entry) => {
                    return (
                      <tr key={entry.id}>
                        {canWrite && (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              aria-label={`Zaznacz pozycję ${entry.kitName ?? entry.labKitId}`}
                              checked={selectedIds.includes(entry.id)}
                              onChange={() => toggleSelect(entry.id)}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2">
                          {entry.kitName ?? `Zestaw #${entry.labKitId}`}
                        </td>
                        <td className="px-3 py-2">{entry.laboratoryName ?? '—'}</td>
                        <td className="px-3 py-2 font-mono">
                          {entry.labKitExpirationId === null
                            ? 'bez partii'
                            : `#${entry.labKitExpirationId}`}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{entry.numberOfKits}</td>
                        <td className="px-3 py-2">{entry.outDate}</td>
                        <td
                          className="max-w-40 truncate px-3 py-2"
                          title={entry.comments ?? undefined}
                        >
                          {entry.comments ?? '—'}
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              aria-label={`Przywróć pozycję ${entry.kitName ?? entry.labKitId}`}
                              disabled={restoreMutation.isPending}
                              onClick={() => {
                                setNotice(null);
                                restoreMutation.mutate(entry.id);
                              }}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                            >
                              <ArchiveRestore className="h-3.5 w-3.5" />
                              Przywróć
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ))}
        </div>
      </div>
    </div>
  );
}
